import { Button, Divider, Stack, useDisclosure } from '@chakra-ui/react';
import { useRef } from 'react';
import { useTranslations } from 'use-intl';

import {
  FundraisingStatus,
  FundraisingType,
  SubscriptionStepStatus,
  UserRole,
  useDeleteSubscriptionMutation,
  useGetSubscriptionQuery,
} from '@blockpulse3/graphql/hooks';
import { DeleteConfirmModal, useErrorToast, useSuccessToast } from '@blockpulse3/ui/commons';
import { useStepStatusMapLazy } from '@blockpulse3/ui/ui-hooks';
import { useUser } from '@blockpulse3/web-client/auth';

type Props = {
  subscriptionId: string;
  onDelete: () => void;
};

export function DeleteSubscriptionButton({ subscriptionId, onDelete }: Props): JSX.Element | null {
  const i18nSubscriberDetailsSidePanel = useTranslations('SubscriberDetailsSidePanel');

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const confirmRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { user } = useUser();
  const { data } = useGetSubscriptionQuery({ variables: { subscriptionId } });
  const [deleteSubscription, { loading: deleteSubscriptionLoading }] =
    useDeleteSubscriptionMutation();

  const subscription = data?.subscription;

  const generateStepStatusMap = useStepStatusMapLazy();
  const [stepStatusMap] = generateStepStatusMap(subscription?.data?.mergedSteps);

  const isUserAuthorized = user?.roles.some((role) =>
    [UserRole.COMPLIANCE_OFFICER, UserRole.SUPERADMIN].includes(role),
  );

  const isCrowdfunding = subscription?.fundraising.type === FundraisingType.CROWDFUNDING;
  const isClosed =
    subscription?.fundraising.status &&
    [FundraisingStatus.CLOSED, FundraisingStatus.REVIEWED, FundraisingStatus.FINALIZED].includes(
      subscription?.fundraising.status,
    );
  const subscriptionSigned = stepStatusMap['DOCUMENT_SIGNATURE'] === SubscriptionStepStatus.VALID;

  const displayDeleteButton =
    isUserAuthorized && isCrowdfunding && !isClosed && !subscriptionSigned;

  if (!displayDeleteButton) return null;

  const handleSubscriberDelete = (): void => {
    if (!isUserAuthorized) return;

    deleteSubscription({
      variables: {
        subscriptionId,
      },
      onCompleted: () => {
        onDelete();
        successToast({ title: i18nSubscriberDetailsSidePanel('Toast.DeleteSubscriptionSuccess') });
        onClose();
      },
      onError: () => {
        errorToast({ title: i18nSubscriberDetailsSidePanel('Toast.DeleteSubscriptionError') });
      },
    });
  };

  return (
    <>
      <Divider />
      <Stack px="4" spacing="4">
        <Button
          alignSelf="end"
          isDisabled={deleteSubscriptionLoading}
          isLoading={deleteSubscriptionLoading}
          mt="4"
          variant="delete"
          w="fit-content"
          onClick={onOpen}
        >
          {i18nSubscriberDetailsSidePanel('DeleteSubscription.ButtonLabel')}
        </Button>
        <DeleteConfirmModal
          isOpen={isOpen}
          leastDestructiveRef={confirmRef}
          subtitle={i18nSubscriberDetailsSidePanel('DeleteSubscription.ConfirmModal.SubTitle')}
          title={i18nSubscriberDetailsSidePanel('DeleteSubscription.ConfirmModal.Title')}
          onClose={onClose}
          onDelete={handleSubscriberDelete}
        />
      </Stack>
    </>
  );
}
